import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Radio, X, Users, Send, Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { useWebSocket } from "@/contexts/WebSocketContext";
import Header from "@/components/Header";

const ICE_SERVERS = [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }];

// Real WebRTC live broadcast page.
// Host: captures camera/mic, creates a peer connection per viewer (signaling via existing /ws).
// Viewer: receives offer from host, answers, attaches remote stream to <video>.
export default function LiveRTC() {
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams();
  const { user } = useAuth();
  const { sendRtc } = useWebSocket();

  const [session, setSession] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [viewerCount, setViewerCount] = useState(0);
  const [showGiftPicker, setShowGiftPicker] = useState(false);
  const [giftCatalog, setGiftCatalog] = useState([]);
  const [coinBalance, setCoinBalance] = useState(0);
  const [floatingGifts, setFloatingGifts] = useState([]);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const peersRef = useRef({}); // host: { viewer_user_id: RTCPeerConnection }
  const peerRef = useRef(null); // viewer: single RTCPeerConnection to host
  const chatPollRef = useRef(null);

  // Fetch session info
  const loadSession = useCallback(async () => {
    if (!urlSessionId) return;
    const r = await fetch(`${API}/live/${urlSessionId}`, { credentials: "include" });
    if (r.ok) {
      const s = await r.json();
      setSession(s);
      setIsHost(s.user_id === user?.user_id);
      setViewerCount(s.viewer_count || 0);
      setChat(s.chat_messages || []);
    }
  }, [urlSessionId, user?.user_id]);

  useEffect(() => { loadSession(); }, [loadSession]);

  // Poll session for chat / viewer count
  useEffect(() => {
    if (!session) return;
    chatPollRef.current = setInterval(loadSession, 3000);
    return () => clearInterval(chatPollRef.current);
  }, [session, loadSession]);

  // ---- Host: start camera + open peer per joining viewer ----
  const startBroadcast = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      setStreaming(true);
      toast.success("Live!");
    } catch (e) {
      toast.error("Camera/mic permission denied");
    }
  };

  const stopBroadcast = async () => {
    Object.values(peersRef.current).forEach(pc => pc.close());
    peersRef.current = {};
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;
    setStreaming(false);
    if (urlSessionId) await fetch(`${API}/live/${urlSessionId}/end`, { method: "POST", credentials: "include" });
    navigate("/live");
  };

  // ---- WebRTC signaling: handle incoming messages ----
  useEffect(() => {
    const handler = async (e) => {
      const msg = e.detail;
      if (!msg || !msg.session_id || msg.session_id !== urlSessionId) return;
      const fromId = msg.from_user_id;

      if (isHost) {
        // Host receives "rtc_join" from viewer → create offer + send back
        if (msg.type === "rtc_join" && localStreamRef.current) {
          let pc = peersRef.current[fromId];
          if (!pc) {
            pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
            peersRef.current[fromId] = pc;
            localStreamRef.current.getTracks().forEach(t => pc.addTrack(t, localStreamRef.current));
            pc.onicecandidate = (ev) => { if (ev.candidate) sendRtc("rtc_ice", fromId, urlSessionId, ev.candidate); };
            pc.onconnectionstatechange = () => {
              if (pc.connectionState === "failed" || pc.connectionState === "disconnected") {
                pc.close(); delete peersRef.current[fromId];
              }
            };
          }
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          sendRtc("rtc_offer", fromId, urlSessionId, offer);
        }
        if (msg.type === "rtc_answer") {
          const pc = peersRef.current[fromId];
          if (pc) await pc.setRemoteDescription(new RTCSessionDescription(msg.payload));
        }
        if (msg.type === "rtc_ice") {
          const pc = peersRef.current[fromId];
          if (pc && msg.payload) try { await pc.addIceCandidate(new RTCIceCandidate(msg.payload)); } catch (e) { console.warn("ICE add failed (non-fatal):", e.message); }
        }
      } else {
        // Viewer receives offer from host → answer
        if (msg.type === "rtc_offer") {
          let pc = peerRef.current;
          if (!pc) {
            pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
            peerRef.current = pc;
            pc.ontrack = (ev) => { if (remoteVideoRef.current) remoteVideoRef.current.srcObject = ev.streams[0]; };
            pc.onicecandidate = (ev) => { if (ev.candidate) sendRtc("rtc_ice", fromId, urlSessionId, ev.candidate); };
          }
          await pc.setRemoteDescription(new RTCSessionDescription(msg.payload));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          sendRtc("rtc_answer", fromId, urlSessionId, answer);
        }
        if (msg.type === "rtc_ice") {
          const pc = peerRef.current;
          if (pc && msg.payload) try { await pc.addIceCandidate(new RTCIceCandidate(msg.payload)); } catch (e) { console.warn("ICE add failed (non-fatal):", e.message); }
        }
      }
    };
    window.addEventListener("rtc_signal", handler);
    return () => window.removeEventListener("rtc_signal", handler);
  }, [isHost, urlSessionId, sendRtc]);

  // ---- Viewer: announce join when session loads ----
  useEffect(() => {
    if (!session || isHost || !session.user_id) return;
    fetch(`${API}/live/${urlSessionId}/join`, { method: "POST", credentials: "include" });
    sendRtc("rtc_join", session.user_id, urlSessionId, null);
  }, [session, isHost, urlSessionId, sendRtc]);

  // Cleanup
  useEffect(() => () => {
    Object.values(peersRef.current).forEach(pc => pc.close());
    if (peerRef.current) peerRef.current.close();
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
  }, []);

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    await fetch(`${API}/live/${urlSessionId}/chat`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: chatInput }),
    });
    setChatInput("");
    loadSession();
  };

  // ---- Gifts: load catalog + balance, send gift to host ----
  const loadGiftData = useCallback(async () => {
    const [cRes, bRes] = await Promise.all([
      fetch(`${API}/gifts/catalog`),
      fetch(`${API}/coins/balance`, { credentials: "include" }),
    ]);
    if (cRes.ok) setGiftCatalog((await cRes.json()).gifts);
    if (bRes.ok) setCoinBalance((await bRes.json()).coin_balance || 0);
  }, []);

  useEffect(() => { loadGiftData(); }, [loadGiftData]);

  const sendGift = async (gift_id, gift_emoji, gift_label) => {
    if (!session?.user_id) return;
    if (session.user_id === user?.user_id) {
      toast.error("You can't send gifts to yourself");
      return;
    }
    try {
      const r = await fetch(`${API}/gifts/send`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          gift_id,
          to_user_id: session.user_id,
          live_session_id: urlSessionId,
        }),
      });
      if (r.status === 402) {
        toast.error("Not enough coins. Buy a pack first.");
        navigate("/gifts");
        return;
      }
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      const d = await r.json();
      toast.success(`${gift_emoji} ${gift_label} sent!`);
      // Floating animation
      const id = Date.now();
      setFloatingGifts((prev) => [...prev, { id, emoji: gift_emoji }]);
      setTimeout(() => setFloatingGifts((prev) => prev.filter((g) => g.id !== id)), 3000);
      setShowGiftPicker(false);
      loadGiftData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #000022 0%, #1a0033 100%)" }}>
      <Header />
      <main className="flex-1 container mx-auto px-4 py-4 max-w-5xl">
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Video panel */}
          <div className="lg:col-span-2">
            <div className="myspace-card relative aspect-video bg-black overflow-hidden">
              {isHost ? (
                <video ref={localVideoRef} autoPlay muted playsInline className="w-full h-full object-cover" data-testid="host-video" />
              ) : (
                <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" data-testid="viewer-video" />
              )}
              {!streaming && isHost && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                  <button onClick={startBroadcast} className="myspace-btn-primary text-lg px-6 py-3 inline-flex items-center gap-2" data-testid="start-broadcast-btn">
                    <Radio className="w-5 h-5" /> Go Live
                  </button>
                </div>
              )}
              {streaming && isHost && (
                <button onClick={stopBroadcast} className="absolute top-3 right-3 bg-[#FF0000] text-white px-3 py-1.5 rounded inline-flex items-center gap-1 text-xs font-bold animate-pulse" data-testid="end-broadcast-btn">
                  <X className="w-3 h-3" /> END
                </button>
              )}
              {!isHost && (
                <div className="absolute top-3 left-3 bg-[#FF0000] text-white px-2 py-1 rounded text-xs font-bold animate-pulse">● LIVE</div>
              )}
              <div className="absolute bottom-3 right-3 bg-black/60 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                <Users className="w-3 h-3" /> {viewerCount}
              </div>
              {/* Floating gift animations */}
              {floatingGifts.map((g) => (
                <div
                  key={g.id}
                  className="absolute bottom-10 left-1/2 -translate-x-1/2 text-6xl pointer-events-none"
                  style={{
                    animation: "floatUp 3s ease-out forwards",
                    filter: "drop-shadow(0 0 12px #FFD700)",
                  }}
                  data-testid="floating-gift"
                >
                  {g.emoji}
                </div>
              ))}
              <style>{`@keyframes floatUp { 0% { opacity: 0; transform: translate(-50%, 0) scale(0.5); } 30% { opacity: 1; transform: translate(-50%, -40px) scale(1.2); } 100% { opacity: 0; transform: translate(-50%, -200px) scale(0.8); } }`}</style>
            </div>
            <h1 className="text-2xl font-bold text-[#00FFFF] mt-3" data-testid="live-title">{session?.title || "Live Broadcast"}</h1>
            <p className="text-sm text-[#FF66FF]">Hosted by {session?.host_name}</p>
          </div>

          {/* Chat panel */}
          <div className="myspace-card flex flex-col" style={{ minHeight: 480, maxHeight: 600 }}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-[#FF00FF]">Live Chat</h3>
              {!isHost && (
                <button
                  onClick={() => setShowGiftPicker(!showGiftPicker)}
                  className="flex items-center gap-1 text-[#FFD700] text-xs font-bold border border-[#FFD700] px-2 py-1 rounded hover:bg-[rgba(255,215,0,0.2)]"
                  data-testid="open-gift-picker-btn"
                  title={`You have ${coinBalance} coins`}
                >
                  <Gift className="w-3 h-3" /> Send Gift ({coinBalance}🪙)
                </button>
              )}
            </div>
            {/* First-time viewer hint */}
            {!isHost && !localStorage.getItem("ourspace_gift_hint_dismissed") && (
              <div
                className="mb-2 px-2 py-1.5 border border-[#FFD700] text-xs text-[#FFD700] flex items-center justify-between"
                style={{ background: "rgba(255,215,0,0.08)" }}
                data-testid="gift-hint-banner"
              >
                <span>💡 Tip the host with a gift! Click 🎁 above.</span>
                <button
                  onClick={() => { localStorage.setItem("ourspace_gift_hint_dismissed", "1"); window.location.reload(); }}
                  data-testid="dismiss-gift-hint"
                  aria-label="Dismiss"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {/* Gift picker grid */}
            {showGiftPicker && !isHost && (
              <div className="grid grid-cols-3 gap-2 p-2 mb-2 border-2 border-[#FFD700] bg-[rgba(255,215,0,0.05)]" data-testid="gift-picker-panel">
                {giftCatalog.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => sendGift(g.id, g.emoji, g.label)}
                    className="flex flex-col items-center p-2 hover:bg-[rgba(255,215,0,0.2)] transition disabled:opacity-50"
                    disabled={coinBalance < g.coins}
                    data-testid={`live-gift-${g.id}`}
                    title={coinBalance < g.coins ? "Not enough coins" : g.label}
                  >
                    <span className="text-2xl">{g.emoji}</span>
                    <span className="text-[10px] text-[#FFD700]">{g.coins}🪙</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex-1 overflow-y-auto space-y-2 mb-2" data-testid="live-chat-list">
              {chat.map((m, i) => (
                <div key={`${m.timestamp || ""}-${m.user_id || i}-${i}`} className="text-sm">
                  <span className="text-[#00FFFF] font-bold">{m.user_name}:</span>{" "}
                  <span className="text-[#FF66FF]">{m.message}</span>
                </div>
              ))}
              {chat.length === 0 && <p className="text-xs text-[#99CCFF]">Be the first to chat!</p>}
            </div>
            <div className="flex gap-2">
              <input value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()}
                placeholder="Say something..." className="flex-1 bg-[rgba(0,0,0,0.4)] border-2 border-[#6600CC] text-[#00FFFF] px-3 py-2 text-sm outline-none focus:border-[#FF00FF]"
                data-testid="live-chat-input"
              />
              <button onClick={sendChat} className="myspace-btn px-3" data-testid="live-chat-send"><Send className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
